/**
 * Centralized error handling utilities
 * Provides consistent error handling across the application
 */

/**
 * Log error to console with context
 * @param {string} context - Where the error occurred (e.g., 'Dashboard', 'Search')
 * @param {Error|string} error - The error object or message
 * @param {Object} metadata - Additional context about the error
 */
export function logError(context, error, metadata = {}) {
  const errorInfo = {
    context,
    timestamp: new Date().toISOString(),
    ...metadata,
  }

  if (error instanceof Error) {
    errorInfo.message = error.message
    errorInfo.stack = error.stack
    console.error(`[${context}] Error:`, error.message, errorInfo)
  } else {
    errorInfo.message = String(error)
    console.error(`[${context}] Error:`, error, errorInfo)
  }

  // In production, could send to error tracking service (Sentry, etc.)
  // if (process.env.NODE_ENV === 'production' && window.Sentry) {
  //   window.Sentry.captureException(error, { contexts: { errorInfo } })
  // }
}

/**
 * Silent error handler for non-critical operations
 * Logs error but doesn't interrupt user flow
 * @param {string} context - Where the error occurred
 * @param {Error|string} error - The error object or message
 */
export function handleSilentError(context, error) {
  logError(context, error, { silent: true })
}

/**
 * Extract user-friendly error message from error object
 * @param {Error|Object|string} error - The error object
 * @param {string} fallbackMessage - Fallback message if extraction fails
 * @returns {string} User-friendly error message
 */
export function extractErrorMessage(error, fallbackMessage = '操作失败，请稍后重试') {
  if (!error) return fallbackMessage

  if (typeof error === 'string') return error

  if (error instanceof Error) return error.message

  if (error.message && typeof error.message === 'string') return error.message

  if (error.detail) {
    if (Array.isArray(error.detail) && error.detail.length > 0) {
      return error.detail.map(e => e.msg || e.message || '').filter(Boolean).join('; ') || fallbackMessage
    }
    if (typeof error.detail === 'string') return error.detail
  }

  return fallbackMessage
}
